import { useCallback, useEffect, useRef, useState } from "react";
import { getSessionVoiceConfig, getVoiceGender } from "@/constants/audioVoice";
import { SCREENS } from "@/features/tutor/constants";
import { useNotepadPerSlideStore } from "@/features/notepad/store/useNotepadPerSlideStore";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore.js";
import { useBiteStatusStore } from "@/features/tutor/store/useBiteStatusStore";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { startTutorTurn } from "@/features/tutor/utils/tutorTurnLifecycle";
import {
  clearLessonStartupPerfAnchor,
  clearSessionStartupPerfAnchor,
  consumeLessonStartupWsOpenToNowMs,
  getLessonStartupPerfNow,
  recordLatestStartupResourceTiming,
  recordLessonStartupPerf,
} from "@/features/tutor/utils/lessonStartupPerf";
import { addResumeDebugEvent } from "@/store/useResumeDebugStore";
import {
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import { tutorApi } from "@/utils/axios-utils";
import { debugLog } from "@/utils/debugLog";
import { resolveAuxMetadataDeferral } from "../contracts/auxMetadataDeferral";
import { useLatestRef } from "./useLatestRef";

export function useVoDemoSessionBoot({
  currentLessonId,
  cachedBitePromptActive = false,
  formData,
  initializeCompletionData,
  ltiLaunchRequestId,
  micPermissionDenied,
  mode,
  onSessionBootComplete,
  onSessionEstablished = () => {},
  // Given a retry when a boot fails; without it the boot falls back to a toast.
  onSessionBootFailed = null,
  onSessionBootStarted = () => {},
  onRestartCleanup = () => {},
  pendingRehydratePayload,
  restartRequestedRef,
  startChoice = null,
  searchParams,
  sessionStartNonce,
  setCurrentCaptions,
  setCurrentScreen,
  setCurrentSlideIndex,
  setSearchParams,
  setSessionId,
  // Defaulted, unlike the other setters: a caller that omits it loses the
  // optimisation and nothing else -- the flag stays down and the block is read.
  setStartupAuxMetadataReady = () => {},
  setTurnId,
  shouldInitializeSession,
  startupAuxMetadataReady = false,
  tvOverrideRef,
  urlModel,
  urlVoiceModel,
  lessonStartupPerfVariant,
}) {
  const initStartedRef = useRef(false);
  const startedSessionRef = useRef(null);
  const lastSessionStartNonceRef = useRef(sessionStartNonce);
  const prevMicDeniedRef = useRef(micPermissionDenied);
  const lastBootFailedRef = useRef(false);
  const [sessionInitRetry, setSessionInitRetry] = useState(0);
  const retryGateRef = useLatestRef({
    currentLessonId,
    shouldInitializeSession,
  });
  const bootCallbacksRef = useLatestRef({
    onSessionBootFailed,
    onSessionBootStarted,
  });

  // Only a failed boot is retried, and only once: the flag drops here, so a
  // second call before the retried boot starts finds nothing to do. Returns
  // whether a boot was started, for a caller that waits on it.
  const retryFailedBoot = useCallback(() => {
    const { currentLessonId, shouldInitializeSession } = retryGateRef.current;
    if (
      !lastBootFailedRef.current ||
      !shouldInitializeSession ||
      initStartedRef.current ||
      !currentLessonId
    ) {
      return false;
    }
    lastBootFailedRef.current = false;
    addResumeDebugEvent("vodemo:initialize_session:retry");
    setSessionInitRetry((count) => count + 1);
    return true;
  }, [retryGateRef]);
  const bootRef = useLatestRef({
    currentLessonId,
    cachedBitePromptActive,
    formData,
    initializeCompletionData,
    ltiLaunchRequestId,
    mode,
    onRestartCleanup,
    onSessionBootComplete,
    onSessionEstablished,
    pendingRehydratePayload,
    restartRequestedRef,
    startChoice,
    searchParams,
    setCurrentCaptions,
    setCurrentScreen,
    setCurrentSlideIndex,
    setSearchParams,
    setSessionId,
    setStartupAuxMetadataReady,
    setTurnId,
    startupAuxMetadataReady,
    tvOverrideRef,
    urlModel,
    urlVoiceModel,
    lessonStartupPerfVariant,
  });

  useEffect(() => {
    if (lastSessionStartNonceRef.current === sessionStartNonce) {
      return;
    }
    lastSessionStartNonceRef.current = sessionStartNonce;
    addResumeDebugEvent("vodemo:session_start_nonce_changed", {
      session_start_nonce: sessionStartNonce,
    });
    initStartedRef.current = false;
    startedSessionRef.current = null;
    setSessionId(null);
  }, [sessionStartNonce, setSessionId]);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const initializeSession = async () => {
      const {
        currentLessonId,
        cachedBitePromptActive,
        formData,
        initializeCompletionData,
        ltiLaunchRequestId,
        mode,
        onRestartCleanup,
        onSessionBootComplete,
        onSessionEstablished,
        pendingRehydratePayload,
        restartRequestedRef,
        startChoice,
        searchParams,
        setCurrentCaptions,
        setCurrentScreen,
        setCurrentSlideIndex,
        setSearchParams,
        setSessionId,
        setStartupAuxMetadataReady,
        setTurnId,
        startupAuxMetadataReady,
        tvOverrideRef,
        urlModel,
        urlVoiceModel,
        lessonStartupPerfVariant,
      } = bootRef.current;

      if (abortController.signal.aborted || !isMounted) {
        return;
      }

      setCurrentCaptions([]);

      if (abortController.signal.aborted || !isMounted) {
        return;
      }

      try {
        debugLog(
          "Registering session with language: ",
          formData.language.value,
          "and voice id: ",
          formData.voice.voiceId,
          "and voice speed: ",
          formData.voice.speed,
        );

        const previewSlideIndex = searchParams.get("slideIndex");
        // Read once, consumed on success only -- see where it is cleared below.
        const isRestartBoot = restartRequestedRef?.current === true;
        const launchClassroomId =
          Number(searchParams.get("classroomId")) || undefined;
        // Deliberately not keyed on the resume intent: whether the tutor greets
        // the student says nothing about whether discovery's aux answers still
        // hold. The resume action does.
        const { canDefer: canDeferAuxMetadata, reason: auxDeferralReason } =
          resolveAuxMetadataDeferral({
            startupAuxMetadataReady,
            startChoice,
            cachedBitePromptActive,
            previewSlideIndex,
            ltiLaunchRequestId,
            mode,
          });
        console.log(
          `[BOOT_DECISION] startChoice=${startChoice} ` +
            `hasPendingRehydrate=${!!pendingRehydratePayload} ` +
            `cachedBitePromptActive=${cachedBitePromptActive} ` +
            `previewSlideIndex=${previewSlideIndex} ` +
            `deferAuxMetadata=${canDeferAuxMetadata} ` +
            `auxDeferralReason=${auxDeferralReason} ` +
            `entryIntent=${searchParams.get("entryIntent")} ` +
            `urlSessionId=${searchParams.get("sessionId")}`,
        );
        addResumeDebugEvent("vodemo:ensure_session:begin", {
          lesson_id: currentLessonId,
          has_pending_rehydrate: !!pendingRehydratePayload,
          start_choice: startChoice,
          preview_slide_index: previewSlideIndex,
          defer_aux_metadata: canDeferAuxMetadata,
          aux_deferral_reason: auxDeferralReason,
        });

        const voiceConfig = formData.voice?.voiceId
          ? getSessionVoiceConfig(formData.voice.voiceId)
          : null;
        const tvOverride = tvOverrideRef.current;
        const ensureUrl = tvOverride
          ? `/session/ensure_session?tutor_version=${encodeURIComponent(tvOverride)}`
          : "/session/ensure_session";
        const ensureStartedAt = getLessonStartupPerfNow();
        recordLessonStartupPerf("ensure_session_begin", {
          lesson_id: currentLessonId,
          variant: lessonStartupPerfVariant,
          start_choice: startChoice,
          mode,
          defer_aux_metadata_requested: canDeferAuxMetadata,
          aux_deferral_reason: auxDeferralReason,
          startup_aux_metadata_ready: startupAuxMetadataReady,
        });
        const response = await tutorApi.post(ensureUrl, {
          lesson_id: currentLessonId,
          model: urlModel,
          // urlVoiceModel is an unrelated React Router path segment (see routes/index.jsx),
          // not derived from voiceConfig — kept separate on purpose.
          voice_model: urlVoiceModel || voiceConfig?.ttsProvider,
          voice_provider: voiceConfig?.ttsProvider ?? "cartesia",
          // "Learn again" is the only path whose lesson has not been erased
          // already; what the student chose travels as start_choice.
          should_reset_runtime_state: isRestartBoot,
          // On every boot, as discovery and the polls send it. The launch
          // class decides whose round a lesson's progress and attempts belong
          // to in No expectation mode; without it the server guesses across
          // the student's classes. A restart also needs it because it
          // replaces the whole session item rather than merging into it, so
          // anything omitted there is erased -- which is also why the voice
          // block above is not optional.
          ...(launchClassroomId != null && {
            classroom_id: launchClassroomId,
          }),
          language: formData.language.value,
          voice_id: voiceConfig?.voiceId ?? undefined,
          voice_gender: formData.voice?.voiceId
            ? getVoiceGender(formData.voice.voiceId)
            : undefined,
          voice_speed: formData.voice?.speed,
          ...(ltiLaunchRequestId && {
            lti_launch_request_id: ltiLaunchRequestId,
          }),
          ...(previewSlideIndex !== null && {
            slide_index: parseInt(previewSlideIndex, 10),
          }),
          defer_aux_metadata: canDeferAuxMetadata,
          // Sent even when the client already refused — the server's own gate
          // can only refuse an intent it is told about.
          start_choice: startChoice,
        });
        recordLessonStartupPerf(
          "ensure_session_success",
          {
            lesson_id: currentLessonId,
            variant: lessonStartupPerfVariant,
            status: response.status,
            defer_aux_metadata_requested: canDeferAuxMetadata,
            startup_aux_metadata_ready: startupAuxMetadataReady,
            aux_metadata_deferred:
              response.data?.aux_metadata_deferred === true,
          },
          ensureStartedAt,
        );
        recordLatestStartupResourceTiming(
          "ensure_session_resource_timing",
          "/session/ensure_session",
          ensureStartedAt,
          {
            lesson_id: currentLessonId,
            variant: lessonStartupPerfVariant,
          },
        );

        if (abortController.signal.aborted || !isMounted) {
          return;
        }

        const { session_id, turn_id, slide_index } = response.data;
        addResumeDebugEvent("vodemo:ensure_session:success", {
          session_id,
          turn_id,
          slide_index,
          aux_metadata_deferred: response.data?.aux_metadata_deferred === true,
        });

        // Restart clears saved boards before publishing the accepted page identity.
        if (isRestartBoot) {
          restartRequestedRef.current = false;
          onRestartCleanup({
            sessionId: session_id,
            slideIndex: slide_index,
            clientFlags: response.data.client_flags,
          });
        }

        setTurnId(turn_id);
        // Session subscribers connect synchronously; publish the accepted turn/activity first.
        setCurrentSlideIndex(slide_index);
        onSessionEstablished({
          lessonId: currentLessonId,
          sessionId: session_id,
        });
        setSessionId(session_id);

        const auxMetadataDeferred =
          response.data?.aux_metadata_deferred === true;
        if (!auxMetadataDeferred) {
          const store = useAssessmentStore.getState();
          if (response.data.has_exit_ticket) {
            store.setMeta("exit_ticket", {
              has: true,
              id: response.data.exit_ticket_id,
              state: response.data.exit_ticket_state,
              attemptsCount: response.data.exit_ticket_attempts_count ?? 0,
            });
          }
          if (response.data.has_pre_assessment) {
            store.setMeta("pre_assessment", {
              has: true,
              id: response.data.pre_assessment_id,
              state: response.data.pre_assessment_state,
              attemptsCount: response.data.pre_assessment_attempts_count ?? 0,
            });
          }
          if (response.data.has_custom_assessment) {
            store.setMeta("custom", {
              has: true,
              id: response.data.custom_assessment_id,
              state: response.data.custom_assessment_state,
              attemptsCount:
                response.data.custom_assessment_attempts_count ?? 0,
            });
          }

          initializeCompletionData(response.data.completed_bite_indices ?? []);

          useBiteStatusStore
            .getState()
            .initFromEnsureSession(response.data.bite_metrics ?? {});

          useNotepadPerSlideStore
            .getState()
            .initFromEnsureSession(response.data.notepad_used_per_slide ?? {});

          // The block just applied is the one this flag stands for, so it is
          // true again. Without this a single refused re-read would stand for
          // the rest of the visit: nothing else raises it, and every later
          // resume would pay for a block the stores already hold.
          setStartupAuxMetadataReady(true);
        } else {
          addResumeDebugEvent("vodemo:ensure_session:aux_metadata_deferred", {
            session_id,
          });
        }

        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("sessionId", session_id);
            newParams.delete("slideIndex");
            return newParams.toString();
          },
          { replace: true },
        );

        useChatSettingsStore.getState().hydrate(session_id);

        const start_request = {
          mode: mode,
          // The fact, not a verdict: the backend reads it both for the aux
          // deferral gate on the boot above and for whether the tutor
          // continues instead of greeting.
          start_choice: startChoice,
          slide_index,
          cached_bite_prompt: cachedBitePromptActive,
          // Echo back this same boot's ensure_session answer rather than
          // letting start_session_lesson re-probe Redis on its own — a
          // second, independent check could disagree with what this
          // response already told the student, purely because Redis's
          // health changed in the gap between the two calls.
          resume_runtime_available: response.data.resume_runtime_available,
        };
        addResumeDebugEvent("vodemo:start_session_lesson:begin", start_request);
        debugLog(`Starting lesson for session ${session_id} with mode ${mode}`);
        console.log(
          `[START_SESSION_LESSON] session_id=${session_id} ` +
            `start_choice=${startChoice} slide_index=${slide_index} ` +
            `cached_bite_prompt=${cachedBitePromptActive} ` +
            `alreadyStarted=${startedSessionRef.current === session_id}`,
        );
        if (startedSessionRef.current !== session_id) {
          startedSessionRef.current = session_id;
          const startLessonStartedAt = getLessonStartupPerfNow();
          const wsOpenToStartSessionLessonMs =
            consumeLessonStartupWsOpenToNowMs(session_id, startLessonStartedAt);
          if (typeof wsOpenToStartSessionLessonMs === "number") {
            recordLessonStartupPerf("ws_open_to_start_session_lesson_ms", {
              lesson_id: currentLessonId,
              session_id,
              variant: lessonStartupPerfVariant,
              duration_ms: Math.round(wsOpenToStartSessionLessonMs * 10) / 10,
            });
          }
          recordLessonStartupPerf("start_session_lesson_begin", {
            lesson_id: currentLessonId,
            session_id,
            variant: lessonStartupPerfVariant,
            start_choice: startChoice,
            mode,
            ws_open_observed: typeof wsOpenToStartSessionLessonMs === "number",
            ...(typeof wsOpenToStartSessionLessonMs === "number"
              ? {
                  ws_open_to_start_session_lesson_ms:
                    Math.round(wsOpenToStartSessionLessonMs * 10) / 10,
                }
              : {}),
          });
          // A manager rebuilt after eviction starts unmuted, and the reporter
          // cannot correct it until the socket is up — by which time the
          // opening response may already have been synthesised.
          await startTutorTurn((tutor_audio_on) =>
            tutorApi.post(`/session/start_session_lesson/${session_id}`, {
              ...start_request,
              tutor_audio_on,
            }),
          );
          recordLessonStartupPerf(
            "start_session_lesson_success",
            {
              lesson_id: currentLessonId,
              session_id,
              variant: lessonStartupPerfVariant,
              start_choice: startChoice,
              mode,
            },
            startLessonStartedAt,
          );
          recordLatestStartupResourceTiming(
            "start_session_lesson_resource_timing",
            `/session/start_session_lesson/${session_id}`,
            startLessonStartedAt,
            {
              lesson_id: currentLessonId,
              session_id,
              variant: lessonStartupPerfVariant,
            },
          );
          addResumeDebugEvent("vodemo:start_session_lesson:success", {
            session_id,
            start_choice: startChoice,
          });
        }

        setCurrentScreen(SCREENS.PROTOTYPE);
        onSessionBootComplete();
      } catch (error) {
        if (abortController.signal.aborted || !isMounted) {
          return;
        }

        console.error("Error initializing session:", error);
        recordLessonStartupPerf("initialize_session_error", {
          lesson_id: currentLessonId,
          variant: lessonStartupPerfVariant,
          status: error?.response?.status,
          message: error?.message,
        });
        clearLessonStartupPerfAnchor();
        clearSessionStartupPerfAnchor();
        addResumeDebugEvent("vodemo:initialize_session:error", {
          status: error?.response?.status,
          message: error?.message,
        });
        onSessionBootComplete();
        initStartedRef.current = false;
        startedSessionRef.current = null;

        if (error.response?.status === 403) {
          // alert() rather than a toast: the redirect below is a full page
          // load, which would wipe a toast before anyone read it.
          alert(
            error.response?.data?.detail ||
              "Your access to tutoring sessions has been suspended. Please contact your teacher.",
          );
          // Back to the classroom the student launched from, not the generic
          // list -- landing on /classrooms after being bounced out of one
          // classroom's lesson loses their place for no reason.
          const deniedClassroomId = searchParams?.get("classroomId");
          window.location.href = deniedClassroomId
            ? `/classroom/${deniedClassroomId}`
            : "/classrooms";
          return;
        }

        lastBootFailedRef.current = true;
        const { onSessionBootFailed } = bootCallbacksRef.current;
        if (onSessionBootFailed) {
          onSessionBootFailed({ retry: retryFailedBoot });
          return;
        }
        // This catch covers ensure_session, start_session_lesson, and the
        // client-side setup between them, so the message can't name a cause.
        useNotificationStore
          .getState()
          .showNotification(
            "Something went wrong starting your lesson. Please refresh and try again.",
            NotificationType.WARNING,
          );
      }
    };

    if (currentLessonId && shouldInitializeSession && !initStartedRef.current) {
      initStartedRef.current = true;
      lastBootFailedRef.current = false;
      bootCallbacksRef.current.onSessionBootStarted();
      initializeSession();
    }

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [
    currentLessonId,
    bootRef,
    bootCallbacksRef,
    mode,
    retryFailedBoot,
    sessionInitRetry,
    sessionStartNonce,
    shouldInitializeSession,
  ]);

  useEffect(() => {
    if (!shouldInitializeSession) {
      initStartedRef.current = false;
      startedSessionRef.current = null;
    }
  }, [shouldInitializeSession]);

  useEffect(() => {
    const wasDenied = prevMicDeniedRef.current;
    prevMicDeniedRef.current = micPermissionDenied;
    if (
      wasDenied &&
      !micPermissionDenied &&
      shouldInitializeSession &&
      !initStartedRef.current &&
      currentLessonId
    ) {
      setSessionInitRetry((count) => count + 1);
    }
  }, [currentLessonId, micPermissionDenied, shouldInitializeSession]);
}
